'use client';

import type { Provider } from '@repo/api-gw-sdk';
import { ResourceType } from '@repo/api-gw-sdk';
import { useMemo } from 'react';

import { useEnvironment } from '@/contexts/useEnvironment';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';

import { ResourceTypes } from './resourceTypes';

export const useSupportedResourceTypes = (
  cloudProviders: Provider[] = []
): ResourceType[] => {
  const { isDemo, isDev } = useEnvironment();
  const {
    AzureSqlDatabaseSupport,
    AzureSqlDiscovery,
    AzureSqlManagedInstanceSupport,
    MySqlFlexibleServerSupport,
    SapHanaPitrSupport,
    CassandraSupport,
    GoogleWorkspaceSupport,
    Microsoft365Support,
    EntraIdSupport,
    AwsEksClusterBackupSupport,
    GcpSqlVirtualMachineSupport,
  } = useFeatureFlags();

  return useMemo(() => {
    const isLocalEnv = isDemo || isDev;
    const resourceTypeEnabled: Partial<Record<ResourceType, boolean>> = {
      [ResourceType.AwsCassandraCluster]: CassandraSupport,
      [ResourceType.GcpCassandraCluster]: CassandraSupport,
      [ResourceType.AzureCassandraCluster]: CassandraSupport,
      [ResourceType.AwsEksCluster]: AwsEksClusterBackupSupport,
      [ResourceType.AzureSapHanaVm]: SapHanaPitrSupport,
      [ResourceType.GcpSapHanaVm]: SapHanaPitrSupport,
      [ResourceType.AzureSqlDatabase]:
        AzureSqlDatabaseSupport || AzureSqlDiscovery,
      [ResourceType.AzureSqlManagedInstance]: AzureSqlManagedInstanceSupport,
      [ResourceType.AzureMysql]: MySqlFlexibleServerSupport,
      [ResourceType.GoogleWorkspaceResource]: GoogleWorkspaceSupport,
      [ResourceType.Microsoft365Resource]: Microsoft365Support,
      [ResourceType.AzureEntraIdResource]: EntraIdSupport,
      [ResourceType.AzureDisk]: false,
      [ResourceType.AzureCosmosdbMongodb]: false,
      [ResourceType.AzureCosmosdbNosql]: false,
      [ResourceType.AzureAksNamespace]: false,
      [ResourceType.GcpGkeNamespace]: false,
      [ResourceType.GcpCloudFirestore]: false,
      // Hidden until SQL Server on GCE can be restored. Coerced because a flag missing from LaunchDarkly is
      // undefined, which the filter below would treat as enabled.
      [ResourceType.GcpSqlVm]: !!GcpSqlVirtualMachineSupport,
    };

    return Object.values(ResourceType).filter(
      (type) =>
        type !== ResourceType.ResourceTypeUnspecified &&
        (cloudProviders.length
          ? cloudProviders.includes(ResourceTypes[type]?.provider)
          : true) &&
        (isLocalEnv || (resourceTypeEnabled[type] ?? true))
    );
  }, [
    CassandraSupport,
    AzureSqlDatabaseSupport,
    AzureSqlDiscovery,
    AzureSqlManagedInstanceSupport,
    MySqlFlexibleServerSupport,
    SapHanaPitrSupport,
    AwsEksClusterBackupSupport,
    GcpSqlVirtualMachineSupport,
    GoogleWorkspaceSupport,
    Microsoft365Support,
    EntraIdSupport,
    isDemo,
    isDev,
    cloudProviders,
  ]);
};
